import type { Command, Directive as Directive_ } from '#~/command/index.js';

/** Hotkey action (subset of `Command`). */
export type Action =
  | Command.CONTROL_GROUP_NEXT
  | Command.CONTROL_GROUP_PREV
  | Command.CONTROL_PRESET_01
  | Command.CONTROL_PRESET_02
  | Command.CONTROL_PRESET_03
  | Command.CONTROL_PRESET_04
  | Command.CONTROL_PRESET_05
  | Command.CONTROL_PRESET_06
  | Command.CONTROL_PRESET_07
  | Command.CONTROL_PRESET_08
  | Command.CONTROL_PRESET_09
  | Command.CONTROL_PRESET_10
  | Command.CONTROL_PRESET_ADD
  | Command.FOCUS_FAR
  | Command.FOCUS_MODE
  | Command.FOCUS_NEAR
  | Command.FOCUS_ONE_PUSH
  | Command.PACE_PT_DOWN
  | Command.PACE_PT_UP
  | Command.PACE_ZOOM_DOWN
  | Command.PACE_ZOOM_UP
  | Command.PTZ_PAN_LEFT
  | Command.PTZ_PAN_MAX
  | Command.PTZ_PAN_MIN
  | Command.PTZ_PAN_RIGHT
  | Command.PTZ_TILT_DOWN
  | Command.PTZ_TILT_MAX
  | Command.PTZ_TILT_MIN
  | Command.PTZ_TILT_UP
  | Command.PTZ_ZOOM_IN
  | Command.PTZ_ZOOM_MAX
  | Command.PTZ_ZOOM_MIN
  | Command.PTZ_ZOOM_OUT
  | Command.TOOL_CENTER
  | Command.TOOL_CINE_CENTER
  | Command.TOOL_CINE_FRAME
  | Command.TOOL_FAST_FRAME
  | Command.TOOL_JOYSTICK
  | Command.TOOL_JOYSTICK_TRIGGER
  | Command.TOOL_TRACKING
  | Command.TOOL_ZOOM
  | Command.TRAY_BOTTOM_TAB
  | Command.TRAY_LEFT_TAB
  | Command.TRAY_RIGHT_TAB
  | Command.VIDEO_ARRANGEMENT
  | Command.VIDEO_SOURCE_01
  | Command.VIDEO_SOURCE_02
  | Command.VIDEO_SOURCE_03
  | Command.VIDEO_SOURCE_04
  | Command.VIDEO_SOURCE_05
  | Command.VIDEO_SOURCE_06
  | Command.VIDEO_SOURCE_07
  | Command.VIDEO_SOURCE_08
  | Command.VIDEO_SOURCE_09
  | Command.VIDEO_SOURCE_10
  | Command.VIDEO_SOURCE_NEXT
  | Command.VIDEO_SOURCE_PREV;

/** Hot key combination. */
export type Combo = Readonly<Pick<KeyboardEvent, 'keyCode'> & { modifiers: number }>;

/** Hot-key directive. */
export type Directive = Directive_<Action, KeyboardEvent>;

/**
 * Cross platform representation of a `KeyboardEvent` or `Combo`. While the returned fingerprint
 * (AKA hash) is a `string`, it is not meant to be human readable. It is strictly used to quickly
 * determine if a `KeyboardEvent` matches `Combo` or if two `Combo` objects are duplicates. It takes
 * into account both differences between macOS and Windows/Linux as well as inconsistencies in how
 * Firefox returns `KeyboardEvent.keyCode` values.
 *
 * There are two rules that allow this to work:
 *
 * - No hot key can have _both_ `Command` and `Control` modifiers.
 * - No two hotkey combinations can only differ in only their `Command` and `Control` modifiers.
 *
 * On macOS, there are four available modifiers and are all treated as first class keys. On
 * non-macOS platforms, the `Control` and `Command` modifiers are treated interchangeably. That is
 * on a non-macOS system, the `Control` key will match `KeyboardEvent` objects on both their
 * `ctrlKey` and `metaKey` modifiers. Because of the two rules above, this will never result in an
 * ambiguity.
 *
 * While irrelevant, the format of the fingerprint consists of four colon separated characters of
 * the form `w:x:y:z` where:
 *
 * - `w` is the `Glyph` (see `constants.ts`) associated with the numeric `keyCode`.
 * - `x` is `1` if the `Command` or `Control` modifier is pressed, `0` otherwise.
 * - `y` is `1` if the `Alt/`Option`modifier is pressed,`0` otherwise.
 * - `z` is `1` if the `Shift` modifier is pressed, `0` otherwise.
 */
export type Fingerprint = string;

/** Hotkey formula. */
export type Formula = Record<Fingerprint, Action>;

/** Hot key mappings. */
export type HotKeys = Record<Action, Combo | false>;

/** Keycode data type. Deprecated but necessary. */
export type KeyCode = KeyboardEvent['keyCode'];

/** Hot key modifiers. */
export type Modifiers = Record<Modifier, boolean>;

/** Hot Key category. */
export enum Category {
  Control = 'c',
  Essentials = 'e',
  Miscellaneous = 'm',
  Sources = 's',
  Toolbar = 't'
}

/** Hot key modifiers bit masks. */
export enum Modifier {
  /** Command `⌘` key on macOS. Not used otherwise. */
  Command = 0x1,

  /** Control key. */
  Control = 0x2,

  /** The `Alt` on Windows and the `Option` key macOS. */
  Option = 0x4,

  /** Shift key, obviously. */
  Shift = 0x8
}
